import React, {useEffect, useRef, useState, useMemo, useCallback} from 'react';
import {Tabs, Button} from 'antd';
import {useTranslation} from 'react-i18next';
import {PodReviewType, PodReviewTabType, PodReviewFilters} from './graphql/types/PodReviewType';
import {PodReviewFiltersTypeInput} from './graphql/queries/getPodReviewOrders';
import {usePodReviewQuery, buildFiltersInputFromFilters} from './hooks/usePodReviewQuery';
import {usePodReviewActions} from './hooks/usePodReviewActions';
import {useQuery} from '@apollo/client';
import {GET_POD_REVIEW_TAB_COUNT, GetPodReviewTabCountResponse} from './graphql/queries/getPodReviewTabCounts';
import PodReviewFilter from './components/PodReviewFilter';
import PodReviewTable from './components/PodReviewTable';
import PodReviewDetailDrawer from './components/PodReviewDetailDrawer';
import {getLastWeekDateTimeRange} from '../../../utils/dateTimeUtils';
import TablePaginationBar from '../../../components/TablePaginationBar';

// 默认筛选条件 - 使用函数返回新对象，避免引用问题
const getDefaultFilters = (): PodReviewFilters => {
    const [start, end] = getLastWeekDateTimeRange();
    return {
        statusUpdatedAt: {
            start,
            end
        },
        podFirstUploadTime: {
            start,
            end
        },
        // 按数据库字段精准匹配
        pickupHubIds: undefined,
        pickupHubSortingCodes: undefined,
        destWarehouseIds: undefined,
        areaSortingCodeList: undefined, // 数组类型，空数组或undefined都表示不筛选
        deliveredDspIds: undefined,
        deliveredDriverAccountId: undefined,
        reviewTime: undefined,
    };
};

const PodReviewPage: React.FC = () => {
    const {t} = useTranslation();
    const [detailVisible, setDetailVisible] = useState(false);
    const [reviewIds, setReviewIds] = useState<string[]>([]);
    const [tempFilters, setTempFilters] = useState<PodReviewFilters>(getDefaultFilters());
    const [filters, setFilters] = useState<PodReviewFilters>(getDefaultFilters());
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<PodReviewType[]>([]);
    const [activeTab, setActiveTab] = useState<PodReviewTabType>(PodReviewTabType.NEWLY_FLAGGED);

    // 使用操作hook
    const { handleDownload, loading: actionLoading} = usePodReviewActions();

    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: PodReviewType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };

    const filtersInput = useMemo(() => buildFiltersInputFromFilters(filters), [filters]);

    // 4个Tab独立计数查询，互不阻塞，始终从网络获取最新数据
    const countQueryOptions = {
        errorPolicy: 'all' as const,
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only' as const,
        nextFetchPolicy: 'network-only' as const,
    };

    const {data: tab1Data, loading: tab1Loading, refetch: refetchTab1} = useQuery<GetPodReviewTabCountResponse>(
        GET_POD_REVIEW_TAB_COUNT,
        {variables: {tabType: PodReviewTabType.NEWLY_FLAGGED, filters: filtersInput}, ...countQueryOptions}
    );
    const {data: tab2Data, loading: tab2Loading, refetch: refetchTab2} = useQuery<GetPodReviewTabCountResponse>(
        GET_POD_REVIEW_TAB_COUNT,
        {variables: {tabType: PodReviewTabType.DRIVER_RESUBMITTED, filters: filtersInput}, ...countQueryOptions}
    );
    const {data: tab3Data, loading: tab3Loading, refetch: refetchTab3} = useQuery<GetPodReviewTabCountResponse>(
        GET_POD_REVIEW_TAB_COUNT,
        {variables: {tabType: PodReviewTabType.AWAITING_DRIVER_UPDATE, filters: filtersInput}, ...countQueryOptions}
    );
    const {data: tab4Data, loading: tab4Loading, refetch: refetchTab4} = useQuery<GetPodReviewTabCountResponse>(
        GET_POD_REVIEW_TAB_COUNT,
        {variables: {tabType: PodReviewTabType.TERMINATED, filters: filtersInput}, ...countQueryOptions}
    );

    // 直接从count query的data和loading状态派生各Tab计数，无useEffect延迟
    // loading时为undefined（计数未知），非loading时取query返回值
    const tabCountDataMap = {
        [PodReviewTabType.NEWLY_FLAGGED]: {data: tab1Data, loading: tab1Loading},
        [PodReviewTabType.DRIVER_RESUBMITTED]: {data: tab2Data, loading: tab2Loading},
        [PodReviewTabType.AWAITING_DRIVER_UPDATE]: {data: tab3Data, loading: tab3Loading},
        [PodReviewTabType.TERMINATED]: {data: tab4Data, loading: tab4Loading},
    };

    const getTabCount = (tab: PodReviewTabType): number | undefined => {
        const {data, loading} = tabCountDataMap[tab];
        if (loading) return undefined; // 正在加载，计数未知
        return data?.getPodReviewTabCount ?? undefined;
    };

    // 当前活跃Tab的计数（直接派生，同渲染周期生效，无useEffect延迟）
    const activeTabCount = getTabCount(activeTab);

    // 列表查询 - useLazyQuery手动触发
    const {
        podReviewData,
        loading,
        executeQuery,
        totalElements
    } = usePodReviewQuery();

    // newFiltersInput: 传入时用新参数refetch，不传时用当前variables refetch（条件没变强制刷新场景）
    const refetchAllCounts = useCallback((newFiltersInput?: PodReviewFiltersTypeInput | undefined) => {
        const overrides = newFiltersInput !== undefined ? {filters: newFiltersInput} : undefined;
        refetchTab1(overrides);
        refetchTab2(overrides);
        refetchTab3(overrides);
        refetchTab4(overrides);
    }, [refetchTab1, refetchTab2, refetchTab3, refetchTab4]);

    const handleFilter = () => {
        const newFilters = {...tempFilters};
        const newFiltersInput = buildFiltersInputFromFilters(newFilters);
        setFilters(newFilters);
        setCurrentPage(1);
        executeQuery(newFilters, 1, pageSize, activeTab);
        refetchAllCounts(newFiltersInput);
    };

    const handleResetFilters = () => {
        const defaultFilters = getDefaultFilters();
        const newFiltersInput = buildFiltersInputFromFilters(defaultFilters);
        setTempFilters(defaultFilters);
        setFilters(defaultFilters);
        setCurrentPage(1);
        executeQuery(defaultFilters, 1, pageSize, activeTab);
        refetchAllCounts(newFiltersInput);
    };

    // Tab切换时重置状态，并行触发count和list查询（同时作为首次挂载的初始加载）
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅在tab切换时触发，filters/pageSize通过闭包捕获当前值即可
    useEffect(() => {
        setCurrentPage(1);
        setSelectedRowKeys([]);
        setSelectedRows([]);
        setReviewIds([]);
        setDetailVisible(false);
        executeQuery(filters, 1, pageSize, activeTab);
        refetchAllCounts();
    }, [activeTab]);

    // 监听筛选条件、分页变化时清空选中行
    useEffect(() => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
        if (detailVisible) {
            setDetailVisible(false);
            setReviewIds([]);
        }
    }, [filters, currentPage, pageSize]);

    // 分页变化时触发列表查询
    const handlePageChange = (page: number, newPageSize: number) => {
        setCurrentPage(page);
        setPageSize(newPageSize);
        executeQuery(filters, page, newPageSize, activeTab);
    };

    // 列表返回的totalElements与tab count不一致时，刷新当前tab计数保持同步
    // 用ref记录已同步的totalElements，避免refetch count后再次触发形成循环
    const lastSyncedTotalRef = useRef<number | null>(null);

    const activeTabRefetchMap = useMemo(() => ({
        [PodReviewTabType.NEWLY_FLAGGED]: refetchTab1,
        [PodReviewTabType.DRIVER_RESUBMITTED]: refetchTab2,
        [PodReviewTabType.AWAITING_DRIVER_UPDATE]: refetchTab3,
        [PodReviewTabType.TERMINATED]: refetchTab4,
    }), [refetchTab1, refetchTab2, refetchTab3, refetchTab4]);

    useEffect(() => {
        if (
            totalElements != null &&
            activeTabCount != null &&
            totalElements !== activeTabCount &&
            lastSyncedTotalRef.current !== totalElements
        ) {
            lastSyncedTotalRef.current = totalElements;
            activeTabRefetchMap[activeTab]();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅在列表数据返回时检查一次
    }, [totalElements]);

    const showOrderDetail = (record: PodReviewType) => {
        // 单个审核：传递包含当前id的数组
        setReviewIds([record.id]);
        setDetailVisible(true);
    };

    const handleBatchReviewClick = async () => {
        if (selectedRows.length === 0) {
            alert(t('podReview.messages.selectAtLeastOneOrder'));
            return;
        }

        // 批量审核：传递选中的id数组
        setReviewIds(selectedRows.map(row => row.id));
        setDetailVisible(true);
    };

    // 下载处理
    const handleDownloadClick = async () => {
        await handleDownload(filtersInput);
    };

    // 状态标签页配置
    const getTabLabel = (tab: PodReviewTabType, labelKey: string) => {
        const {data, loading: isLoading} = tabCountDataMap[tab];
        const count = data?.getPodReviewTabCount;
        const displayCount = isLoading || count == null ? '...' : count;
        return `${t(labelKey)} (${displayCount})`;
    };

    const statusTabs = [
        {
            key: PodReviewTabType.NEWLY_FLAGGED.toString(),
            label: getTabLabel(PodReviewTabType.NEWLY_FLAGGED, 'podReview.tabs.newlyFlagged'),
        },
        {
            key: PodReviewTabType.AWAITING_DRIVER_UPDATE.toString(),
            label: getTabLabel(PodReviewTabType.AWAITING_DRIVER_UPDATE, 'podReview.tabs.awaitingDriverUpdate'),
        },
        {
            key: PodReviewTabType.DRIVER_RESUBMITTED.toString(),
            label: getTabLabel(PodReviewTabType.DRIVER_RESUBMITTED, 'podReview.tabs.driverResubmitted'),
        },
        {
            key: PodReviewTabType.TERMINATED.toString(),
            label: getTabLabel(PodReviewTabType.TERMINATED, 'podReview.tabs.terminated'),
        },
    ];

    return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                {/* 筛选条件 */}
                <PodReviewFilter
                    filters={tempFilters}
                    setFilters={setTempFilters}
                    handleFilter={handleFilter}
                    handleResetFilters={handleResetFilters}
                    selectedRows={selectedRows}
                />

                {/* 状态标签页 */}
                <div className="mb-4">
                    <Tabs
                        activeKey={activeTab.toString()}
                        onChange={(key) => setActiveTab(Number(key) as PodReviewTabType)}
                        items={statusTabs}
                        className="pod-review-tabs [&_.ant-tabs-tab]:font-bold"
                    />
                </div>

                {/* 批量操作区域 */}
                <div className="flex justify-end items-center mb-3 pl-0">
                    {/* 右侧 - 所有按钮 */}
                    <div className="flex items-center gap-2">
                        {(activeTab === PodReviewTabType.NEWLY_FLAGGED || activeTab === PodReviewTabType.DRIVER_RESUBMITTED || activeTab === PodReviewTabType.TERMINATED) && (
                            <Button
                                type="primary"
                                onClick={handleBatchReviewClick}
                                loading={actionLoading}
                                disabled={selectedRows.length === 0}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('podReview.batchReview')} ({selectedRows.length})</span>
                            </Button>
                        )}
                        {activeTab === PodReviewTabType.TERMINATED && (
                            <Button
                                type="primary"
                                onClick={handleDownloadClick}
                                loading={actionLoading}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('podReview.download')}</span>
                            </Button>
                        )}
                        <Button
                            type="primary"
                            onClick={handleFilter}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('podReview.filter.title')}</span>
                        </Button>
                        <Button
                            onClick={handleResetFilters}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('podReview.filter.reset')}</span>
                        </Button>
                    </div>
                </div>

                {/* POD Review 列表 */}
                <PodReviewTable
                    podReviewData={podReviewData}
                    showOrderDetail={showOrderDetail}
                    onSelectChange={handleSelectChange}
                    selectedRowKeys={selectedRowKeys}
                    loading={loading}
                    showActionColumn={true}
                    isEditReviewButton={activeTab === PodReviewTabType.AWAITING_DRIVER_UPDATE || activeTab === PodReviewTabType.TERMINATED}
                    showRowSelection={activeTab === PodReviewTabType.NEWLY_FLAGGED || activeTab === PodReviewTabType.DRIVER_RESUBMITTED || activeTab === PodReviewTabType.TERMINATED}
                />

                {/* 分页 */}
                <TablePaginationBar
                    total={totalElements ?? activeTabCount ?? 0}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                />

                {/* POD Review 详情抽屉组件 */}
                <PodReviewDetailDrawer
                    visible={detailVisible}
                    onClose={() => {
                        // 关闭抽屉，清理所有状态
                        setDetailVisible(false);
                        setReviewIds([]);
                        setSelectedRowKeys([]);
                        setSelectedRows([]);
                        executeQuery(filters, currentPage, pageSize, activeTab);
                        refetchAllCounts();
                    }}
                    reviewIds={reviewIds}
                    isEditMode={activeTab === PodReviewTabType.AWAITING_DRIVER_UPDATE || activeTab === PodReviewTabType.TERMINATED}
                />
            </div>
    );
};

export default PodReviewPage;
